import { Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import App from "../App";
import EarningsCalculator from "../components/EarningsCalculator/EarningsCalculator";
import { ROUTE_PATHS } from "../common/enums/routing-enums";
import CSI from "../components/CustomerSalesIncentive/CustomerSalesIncentive";
import FAA from "../components/FAA/FAA";
import ProvideUsFeedback from "../components/Resources/ProvideUsFeedback/ProvideUsFeedback";
import LogOut from "../components/LogOut/LogOut";
import LOS from "../components/RwLos/RwLos";
import EdLos from "../components/EdLos/EdLos";
import Watchlist from "../components/Watchlist/Watchlist";
import { HomePage } from "../components/HomePage/HomePage";
import { ErrorPage, ERROR_TYPE } from "../components/ErrorPage/ErrorPage";
import { FullPageComponentLoader } from "../common/components/FullPageComponentLoader/FullPageComponentLoader";
import FrontlineQ from "../components/FrontlineQ/FrontlineQ";
import { CustomersRoutes } from "./SubRoutes/Customers";
import { MBIRoutes } from "./SubRoutes/MBI";
import { ActionReportsRoutes } from "./ActionReportsRoutes";
import { ErrorRoutes } from "./ErrorRoutes";
import { AuthenticationRoutes } from "./AuthenticationRoutes";
import { HatRoutes } from "./SubRoutes/Hat";
import { GarRoutes } from "./SubRoutes/Hat";
import SSIUplineReport from "../components/SSIUpline/SSIUplineReport";
import { LandingPage } from "../components/LandingPage/LandingPage";
import { GroupManagementDashboard } from "../components/LandingPage/GroupManagementDashboard/GroupManagementDashboard";

const PathToBronze = lazy(() => import("../components/BronzeIncentives/PathToBronze/PathToBronze"));
const BronzeIncentives = lazy(() => import("../components/BronzeIncentives/BronzeIncentives"));
const StrongStart = lazy(() => import("../components/StrongStart/StrongStart"));
const BronzeGroupTracking = lazy(() => import("../components/BronzeGroupTracking/BronzeGroupTracking"));
const PerformancePlusElite = lazy(() => import("../components/PerfPlusElite/PerfPlusElite"));
const MonthlyPGG = lazy(() => import("../components/PGG/MonthlyPGG/MonthlyPGG"));
const AnnualPGG = lazy(() => import("../components/PGG/AnnualPGG/AnnualPGG"));
const GcrPGG = lazy(() => import("../components/PGG/GCRPGG/GCRPGG"));
const FGG = lazy(() => import("../components/FrontlineGroupGrowth/FrontlineGroupGrowth"));
const TwoTimeCashIncentive = lazy(() => import("../components/TwoTimeCashIncentives/TwoTimeCashIncentive"));
const LeadershipSeminar = lazy(() => import("../components/LeadershipSeminar/LeadershipSeminar"));
const LeaderIncentive = lazy(() => import("../components/LeaderIncentive/LeaderIncentive"));
const QCGrowth = lazy(() => import("../components/QCGrowth/QCGrowth"));
const VisionBonus = lazy(() => import("../components/VisionBonus/VisionBonus"));
const MonthlyQ = lazy(() => import("../components/MonthlyQ/MonthlyQ"));
const QGrowthIncentive = lazy(() => import("../components/QGrowthIncentive/QGrowthIncentive"));
const HealthWellbeing = lazy(() => import("../components/HealthWellbeing/HealthWellbeing"));
const PerformancePlus = lazy(() => import("../components/PerformancePlus/PerformancePlus"));
const AnnualQ = lazy(() => import("../components/AnnualQ/AnnualQ"));
const DiamondBonus = lazy(() => import("../components/100DiamondBonus/100DiamondBonus"));
const OnlineStatementsPage = lazy(() => import("../components/OnlineStatements/OnlineStatementsPage"));
const RetroPvInquiry = lazy(() => import("../components/RetroPvInquiry/RetroPvInquiry"));
const FoundersSalesIncentive = lazy(() => import("../components/FoundersSalesIncentive/FoundersSalesIncentive"));

export const Routing = () => {
  return (
    <Routes>
      <Route path="/" element={<App />}>
        {/* Performance Routes */}
        <Route
          path={ROUTE_PATHS.PERFORMANCE_DASHBOARD}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <LandingPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATHS.GROUP_MANAGEMENT_DASHBOARD}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <GroupManagementDashboard />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATHS.EARNINGS_CALCULATOR}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <EarningsCalculator />
            </Suspense>
          }
        />
        {MBIRoutes()}
        {/* Early Incentives Routes */}
        <Route path={ROUTE_PATHS.CSI} element={<CSI />} />
        <Route
          path={ROUTE_PATHS.BRONZE_INCENTIVES}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <BronzeIncentives />
            </Suspense>
          }
        />
        <Route path={ROUTE_PATHS.NEW_PATH_TO_BRONZE} element={<PathToBronze />} />
        <Route path={ROUTE_PATHS.STRONG_START_INCENTIVE} element={<StrongStart />} />
        <Route path={ROUTE_PATHS.SSI_UPLINE_REPORT} element={<SSIUplineReport />} />
        <Route
          path={ROUTE_PATHS.FOUNDERS_SALES_INCENTIVE}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <FoundersSalesIncentive />
            </Suspense>
          }
        />
        {/* My Group Routes */}
        {CustomersRoutes()}
        {/* !!important!! DO NOT remove this redirect route, it is for markets that still using old link to re-route to new link */}
        <Route path={ROUTE_PATHS.CML} element={<Navigate to={ROUTE_PATHS.CUSTOMER_LIST} replace />} />
        <Route path={ROUTE_PATHS.LOS} element={<LOS />} />
        <Route path={ROUTE_PATHS.EDLOS} element={<EdLos />} />
        {ActionReportsRoutes()}
        <Route path={ROUTE_PATHS.WATCHLIST} element={<Watchlist />} />
        <Route
          path={ROUTE_PATHS.BRONZE_GROUP_TRACKING}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <BronzeGroupTracking />
            </Suspense>
          }
        />

        {/* Leader Growth Routes */}
        <Route path={ROUTE_PATHS.PPE} element={<PerformancePlusElite />} />
        <Route path={ROUTE_PATHS.MONTHLY_Q} element={<MonthlyQ />} />
        <Route
          path={ROUTE_PATHS.Q_GROWTH_INCENTIVE}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <QGrowthIncentive />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATHS.EARLY_Q_BOOSTER}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <QGrowthIncentive />
            </Suspense>
          }
        />
        <Route path={ROUTE_PATHS.MONTHLY_PGG} element={<MonthlyPGG />} />
        <Route path={ROUTE_PATHS.ANNUAL_PGG} element={<AnnualPGG />} />
        <Route path={ROUTE_PATHS.PGG_GCR} element={<GcrPGG />} />
        <Route path={ROUTE_PATHS.FGG} element={<FGG />} />
        <Route path={ROUTE_PATHS.FRONTLINE_Q} element={<FrontlineQ />} />
        <Route path={ROUTE_PATHS.LEADERSHIP_PLUS} element={<FGG />} />
        <Route path={ROUTE_PATHS.TWO_TIME_CASH_INCENTIVE} element={<TwoTimeCashIncentive />} />
        <Route path={ROUTE_PATHS.HEALTH_WELLBEING} element={<HealthWellbeing />} />
        {HatRoutes()}
        {GarRoutes()}
        <Route path={ROUTE_PATHS.FAA} element={<FAA />} />
        <Route path={`${ROUTE_PATHS.FAA}/*`} element={<FAA />} />
        <Route path={ROUTE_PATHS.LEADERSHIP_SEMINAR} element={<LeadershipSeminar />} />
        <Route
          path={ROUTE_PATHS.LEADER_INCENTIVE}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <LeaderIncentive />
            </Suspense>
          }
        />
        <Route path={ROUTE_PATHS.QC_GROWTH} element={<QCGrowth />} />
        <Route path={ROUTE_PATHS.VISION_BONUS} element={<VisionBonus />} />
        <Route path={ROUTE_PATHS.PERFORMANCE_PLUS} element={<PerformancePlus />} />
        <Route path={ROUTE_PATHS.ANNUALQ} element={<AnnualQ />} />
        <Route path={ROUTE_PATHS.DIAMOND_BONUS} element={<DiamondBonus />} />
        {/* Online Statements */}
        <Route
          path={ROUTE_PATHS.ONLINE_STATEMENTS}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <OnlineStatementsPage />
            </Suspense>
          }
        />
        {/* Performance & Income Routes */}
        <Route
          path={ROUTE_PATHS.RETRO_PV_INQUIRY}
          element={
            <Suspense fallback={<FullPageComponentLoader />}>
              <RetroPvInquiry />
            </Suspense>
          }
        />
        {/* Resources Routes */}
        <Route path={ROUTE_PATHS.FEEDBACK} element={<ProvideUsFeedback />} />
        <Route path={ROUTE_PATHS.LOG_OUT} element={<LogOut />} />
        {/* Other */}
        {ErrorRoutes()}
        {AuthenticationRoutes()}
        <Route path="/" element={<HomePage />} />
        <Route path="*" element={<ErrorPage errorType={ERROR_TYPE.PAGE_UNAVAILABLE} />} />
      </Route>
    </Routes>
  );
};
